import { OverlayOutsideClickDispatcher, OverlayRef } from '@angular/cdk/overlay';
import { Injectable, NgZone } from '@angular/core';

type PointerListener = (event: Event) => void;

const EVENTS: Array<{ type: string; listener: '_pointerDownListener' | '_clickListener' }> = [
  { type: 'pointerdown', listener: '_pointerDownListener' },
  { type: 'click', listener: '_clickListener' },
  { type: 'auxclick', listener: '_clickListener' },
  { type: 'contextmenu', listener: '_clickListener' },
];

/**
 * Kliknutí mimo plovoucí vrstvu CDK poslouchané na `<html>`, ne na `<body>` (EL-2342).
 *
 * ⚠ Totéž, co `HtmlOverlayKeyboardDispatcher` řeší pro klávesy. Vrstvy visí od EL-2271
 * pod `<html>` (`HtmlOverlayContainerService`), CDK ale kliknutí „mimo vrstvu" čte
 * na `<body>`. Kliknutí **uvnitř jiné vrstvy** — typicky do zásuvky s formulářem —
 * k posluchači nedošlo, takže nabídka `nz-select` otevřená v zásuvce zůstala viset:
 * nezavřel ji klik na jiné pole, na popisek ani do prázdna a překrývala tlačítka
 * Zrušit / Přidat. Zavřel ji jen Escape nebo klik mimo zásuvku (připomínka zadavatele,
 * přidání zaměstnance).
 *
 * Posluchače jsou tytéž (soukromá pole CDK), jen visí o úroveň výš a ve fázi zachycení
 * jako v CDK. Přesouvají se po každém `add` — `nz-drawer` přidává svůj overlay znovu
 * a CDK by je při tom připojil zpátky na `<body>` (viz klávesový dispečer).
 *
 * ⚠ Z `<body>` se odpojují úklidovými funkcemi CDK (`_cleanups`), ne
 * `removeEventListener` — od CDK 19 je renderer obaluje (viz klávesový dispečer, EL-2338).
 */
@Injectable()
export class HtmlOverlayOutsideClickDispatcher extends OverlayOutsideClickDispatcher {

  override add(overlayRef: OverlayRef): void {
    super.add(overlayRef);

    if (this._isAttached) {
      const root = this._document.documentElement;

      const cdk = this as unknown as { _cleanups?: Array<() => void> };
      cdk._cleanups?.forEach((cleanup) => cleanup());
      cdk._cleanups = undefined;

      const attach = () => EVENTS.forEach(({ type, listener }) => {
        root.addEventListener(type, this.listenerOf(listener), true);
      });

      if (this.zone) {
        this.zone.runOutsideAngular(attach);
      } else {
        attach();
      }
    }
  }

  override detach(): void {
    if (this._isAttached) {
      const root = this._document.documentElement;

      EVENTS.forEach(({ type, listener }) => root.removeEventListener(type, this.listenerOf(listener), true));
    }

    super.detach();
  }

  /** Posluchače z CDK — soukromá pole předka, proto přes index. */
  private listenerOf(name: '_pointerDownListener' | '_clickListener'): PointerListener {
    return (this as unknown as Record<string, PointerListener>)[name];
  }

  private get zone(): NgZone | undefined {
    return (this as unknown as { _ngZone?: NgZone })._ngZone;
  }
}
